import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, EmptyState, Icon, Skeleton, Button } from "@sagot/ui";
import { useMemo, useState } from "react";
import { useWorkspaceContext } from "../../../../session/workspace-context";
import { moduleOfTrigger, useWorkflowModules } from "../service/module-model";
import { useServiceDesk } from "../service/use-service-desk";
import type { WorkflowOut } from "../types";
import { DefaultCard } from "./default-card";
import { DefaultsToolbar } from "./defaults-toolbar";
import { filterDefaults, groupByArea, isFiltering, NO_FILTERS, pausedDefaults, type ListFilters } from "./defaults-model";
import { PausedBanner } from "./paused-banner";
import { useDefaultCatalogue } from "./use-defaults";

const D = "engage.workflows.defaults";
const L = `${D}.service`;

interface DefaultsSectionProps {
  /** The defaults, or nothing while the list loads. */
  defaults: readonly WorkflowOut[] | undefined;
  canManage: boolean;
  canPublish: boolean;
  /** The plan has no workflows of the workspace's own, so a default cannot be duplicated. */
  duplicateLocked: boolean;
}

function SectionSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" aria-hidden="true">
      {Array.from({ length: 3 }, (_, index) => <Skeleton key={index} className="h-36 rounded-card" />)}
    </div>
  );
}

/** The step types of each default as the catalogue ships it, by key, in path order: what a card says the default does. */
function useShippedSteps(enabled: boolean): ReadonlyMap<string, readonly string[]> {
  const catalogue = useDefaultCatalogue(enabled);
  return useMemo(() => {
    const steps = new Map<string, readonly string[]>();
    for (const item of catalogue.data?.items ?? []) {
      const paths = (item.definition as { paths?: Record<string, { steps?: { type?: string }[] }> }).paths ?? {};
      const types = Object.keys(paths).sort().flatMap((id) => (paths[id]?.steps ?? []).flatMap((step) => (step.type ? [step.type] : [])));
      steps.set(item.system_key, types);
    }
    return steps;
  }, [catalogue.data]);
}

/** Shown where the Service desk defaults would be when the plan lacks the Service Desk: what they do, and the way up. */
function ServiceLockedCard({ plan }: { plan: string }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-line bg-surface-2 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="service-locked">
      <div className="flex min-w-0 items-start gap-3">
        <Icon name="lock" className="mt-0.5 size-5 shrink-0 text-fg-subtle" />
        <div className="min-w-0">
          <h3 className="text-sm font-bold">{t(`${L}.locked.title`, { plan: t(`console.plans.${plan}`) })}</h3>
          <p className="mt-0.5 text-sm text-fg-muted">{t(`${L}.locked.body`)}</p>
        </div>
      </div>
      <AppLink className="btn btn-secondary btn-sm shrink-0" href={consoleRoutes.billing(summary.slug)}>{t(`${L}.locked.cta`)}</AppLink>
    </div>
  );
}

/** "Default workflows": what the workspace is given, grouped by area, with a banner when one that normally runs is paused. */
export function DefaultsSection({ defaults, canManage, canPublish, duplicateLocked }: DefaultsSectionProps) {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<ListFilters>(NO_FILTERS);
  const modules = useWorkflowModules();
  const serviceDesk = useServiceDesk();
  const shipped = useShippedSteps(defaults !== undefined);
  const moduleOffOf = (row: WorkflowOut) => {
    const module = moduleOfTrigger(row.trigger);
    return module !== undefined && modules.known && modules.isOff(module) ? module : null;
  };
  const all = defaults ?? [];
  const groups = groupByArea(filterDefaults(all, filters));
  const everyArea = groupByArea(all).map((group) => group.area);
  const shown = groups.reduce((sum, group) => sum + group.rows.length, 0);
  const hasServiceArea = everyArea.includes("service_desk");
  const filtering = isFiltering(filters);
  return (
    <section className="card card-body space-y-5" aria-labelledby="defaults-title" data-testid="defaults-section">
      <div>
        <h2 id="defaults-title" className="text-base font-bold">{t(`${D}.title`)}</h2>
        <p className="mt-0.5 text-sm text-fg-muted">{t(`${D}.intro`)}</p>
      </div>
      {defaults === undefined ? <SectionSkeleton /> : null}
      {defaults ? <PausedBanner paused={pausedDefaults(defaults).filter((row) => moduleOffOf(row) === null)} canPublish={canPublish} /> : null}
      {defaults && defaults.length > 0 ? <DefaultsToolbar filters={filters} areas={everyArea} shown={shown} total={all.length} onChange={setFilters} /> : null}
      {defaults && !hasServiceArea && serviceDesk.state === "locked" ? <ServiceLockedCard plan={serviceDesk.minPlan} /> : null}
      {groups.map(({ area, rows }) => {
        const total = groupByArea(all).find((group) => group.area === area)?.rows.length ?? rows.length;
        return (
          <div key={area} className="space-y-3" data-testid={`defaults-area-${area}`}>
            <div>
              <h3 className="text-sm font-bold">
                {t(area === "orders_field_service" ? `${L}.areas.${area}` : `${D}.areas.${area}`)} <span className="text-fg-subtle">({filtering && rows.length !== total ? t(`${L}.filters.count`, { shown: rows.length, total }) : rows.length})</span>
              </h3>
              {area === "service_desk" || area === "orders_field_service" ? <p className="text-xs text-fg-subtle">{t(`${L}.areaNotes.${area}`)}</p> : null}
            </div>
            <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {rows.map((row) => (
                <DefaultCard
                  key={row.id}
                  workflow={row}
                  canManage={canManage}
                  canPublish={canPublish}
                  duplicateLocked={duplicateLocked}
                  steps={row.is_modified ? undefined : row.system_key ? shipped.get(row.system_key) : undefined}
                  moduleOff={moduleOffOf(row)}
                />
              ))}
            </ul>
          </div>
        );
      })}
      {defaults && defaults.length > 0 && shown === 0 ? (
        <EmptyState
          icon="search"
          title={t(`${L}.filters.emptyTitle`)}
          description={t(`${L}.filters.emptyBody`)}
          action={<Button variant="secondary" onClick={() => { setFilters(NO_FILTERS); }}>{t(`${L}.filters.clear`)}</Button>}
        />
      ) : null}
    </section>
  );
}
